/* eslint-disable react/prop-types */
/**
 * A single remembered map view (centre + zoom + base layer) shared by the three
 * Maps-hub tabs: My Property, Survey Data and Crowd Data.
 *
 * Each tab mounts its own <MapContainer>, so switching tabs unmounts one map and
 * boots the next from its hardcoded default (geo-IP / Hyderabad at zoom 9),
 * throwing away wherever the user had panned to. Persisting the view here makes
 * the hub read as ONE map with three tool sets layered over it: pan to a plot on
 * My Property, switch to Crowd Data, and you're still looking at that plot.
 *
 * Persistence is localStorage (same as the hub's other prefs, `surveyTool` /
 * `crowdRailTab`), so the view also survives a reload and a return visit.
 *
 * Opt-in only. Every consumer passes an explicit flag, the many address-picker
 * maps that share <LocationPickerMap> / <MapLayers> must keep booting on the
 * address they're editing, not on the hub's last view.
 */
import { useEffect, useRef, useState } from "react";
import { useMap, useMapEvents, CircleMarker, Tooltip } from "react-leaflet";

const VIEW_KEY = "mapsHubView";
const BASE_KEY = "mapsHubBase";

// The hub's maps are all locked to India (see LockToIndia / INDIA_BOUNDS). A
// stored view outside India is junk (hand-edited storage, or a bounds change
// since it was written), so it's dropped; a zoom deeper than MAX_ZOOM (the
// Measure map goes further) is clamped, so the view still opens on the spot.
const INDIA = { minLat: 6.0, maxLat: 37.6, minLng: 67.0, maxLng: 98.0 };
const MIN_ZOOM = 3;
const MAX_ZOOM = 18;

const inIndia = (lat, lng) =>
  lat >= INDIA.minLat && lat <= INDIA.maxLat && lng >= INDIA.minLng && lng <= INDIA.maxLng;

/**
 * The last view the user left any hub map on, or null when nothing valid is
 * stored. Shape: `{ lat, lng, zoom }`.
 */
export function readMapView() {
  try {
    const raw = localStorage.getItem(VIEW_KEY);
    if (!raw) return null;
    const { lat, lng, zoom } = JSON.parse(raw) || {};
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;
    if (!inIndia(lat, lng) || zoom < MIN_ZOOM) return null;
    // The Measure map zooms past 18 to place points precisely; come back to
    // the same spot at the hub's usual cap rather than dropping it (which
    // used to throw the user back to the default city).
    return { lat, lng, zoom: Math.min(zoom, MAX_ZOOM) };
  } catch {
    return null; // unparseable / storage disabled → fall back to the tab default
  }
}

export function writeMapView({ lat, lng, zoom }) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return;
  try {
    localStorage.setItem(VIEW_KEY, JSON.stringify({ lat, lng, zoom }));
  } catch {
    /* private mode / quota, remembering the view is best-effort */
  }
}

/** The last base layer chosen on a hub map, or `fallback` when none is stored. */
export function readMapBase(fallback) {
  try {
    return localStorage.getItem(BASE_KEY) || fallback;
  } catch {
    return fallback;
  }
}

export function writeMapBase(name) {
  if (!name) return;
  try {
    localStorage.setItem(BASE_KEY, name);
  } catch {
    /* best-effort, see writeMapView */
  }
}

/**
 * Drop inside a hub <MapContainer> to record its centre/zoom as the user moves.
 * `moveend` covers drags, `flyTo`/`setView` and the tail of a zoom; `zoomend` is
 * bound too so a zoom that leaves the centre untouched is still captured.
 *
 * Pass `enabled={false}` to mount it inert on a shared map that is being used
 * outside the hub (e.g. <LocationPickerMap> in an address form).
 */
export function PersistMapView({ enabled = true }) {
  const map = useMap();
  const save = () => {
    if (!enabled) return;
    const c = map.getCenter();
    writeMapView({ lat: c.lat, lng: c.lng, zoom: map.getZoom() });
  };
  useMapEvents({ moveend: save, zoomend: save });

  // Record the boot view too, so a tab that lands on its own default (nothing
  // stored yet) seeds the shared view instead of leaving the next tab to guess.
  useEffect(() => {
    save();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// ── Live "fly to a place" broadcast ──────────────────────────────────────────
// A tiny pub/sub so a control OUTSIDE the map (the Maps-hub toolbar search) can
// pan whichever map is currently mounted. `flyToPlace` broadcasts a target; each
// map mounts <FlyToOnBroadcast/> to receive it and fly there.
const _flyListeners = new Set();
export function flyToPlace(target) {
  if (!target || !Number.isFinite(target.lat) || !Number.isFinite(target.lng))
    return;
  _flyListeners.forEach((cb) => {
    try {
      cb(target);
    } catch {
      /* a dead listener shouldn't stop the others */
    }
  });
}
// ── "a PLACE was picked" broadcast ───────────────────────────────────────────
// `flyToPlace` above only moves the view, which is all the hub toolbar needed
// while its maps had no notion of a selected point. A picker tab (Crowd Data)
// also wants to PIN what was searched — but only for a real geocoded place, not
// for the tab's own rows (those open a drawer instead). So picking a place is a
// separate signal rather than a flag on the fly broadcast.
const _placeListeners = new Set();

/**
 * Announce that the user picked a geocoded place.
 * @param {{lat:number,lng:number,display_name?:string,boundingbox?:string[]}} place
 */
export function notifyPlacePicked(place) {
  if (!place || !Number.isFinite(place.lat) || !Number.isFinite(place.lng)) return;
  _placeListeners.forEach((cb) => {
    try {
      cb(place);
    } catch {
      /* a dead listener shouldn't stop the others */
    }
  });
}

/** Subscribe to place picks for as long as the component is mounted. */
export function useOnPlacePicked(handler) {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const cb = (place) => ref.current?.(place);
    _placeListeners.add(cb);
    return () => _placeListeners.delete(cb);
  }, []);
}

/**
 * The dot a searched place leaves behind on whichever hub map is mounted.
 *
 * The hub toolbar's search only moved the camera, so on every tab the map flew
 * somewhere and marked nothing — "it redirected but no point is shown". This
 * marks the place that was flown to, so the redirect always lands on something
 * visible, on My Property and Survey Data as much as on Crowd Data.
 *
 * Listens to PLACE picks only (not every `flyToPlace`): flying to one of a tab's
 * own rows must not add a second marker on top of the one that row already has.
 */
export function SearchedPlaceDot() {
  const [place, setPlace] = useState(null);
  useOnPlacePicked((p) => setPlace(p));
  if (!place) return null;
  return (
    <CircleMarker
      center={[place.lat, place.lng]}
      radius={7}
      interactive={!!place.display_name}
      pathOptions={{
        color: "#ffffff",
        weight: 3,
        fillColor: "#2563EB",
        fillOpacity: 1,
      }}
    >
      {place.display_name && (
        <Tooltip direction="top" offset={[0, -8]}>
          {place.display_name}
        </Tooltip>
      )}
    </CircleMarker>
  );
}

export function FlyToOnBroadcast({ zoom = 15 }) {
  const map = useMap();
  useEffect(() => {
    const cb = ({ lat, lng, zoom: z }) => map.flyTo([lat, lng], z || zoom);
    _flyListeners.add(cb);
    return () => _flyListeners.delete(cb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
}
